import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import LHModalPop from '../../../components/LHModalPop';
import { RootState } from '../../../store/index';
import { hideErrorModal, removeErrorById } from '../../../store/slices/standUpSlice';

/**
 * Convert API name to user-friendly title format
 * Example: 'fetchStandUpData' -> 'Fetch Stand Up Data Failed'
 */
const getApiTitle = (apiName: string): string => {
  // Convert camelCase to words and capitalize
  const words = apiName
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
  
  return `${words} Failed`;
};

/**
 * Centralized Error Modal for StandUp Operations
 * 
 * This component displays API errors in a user-friendly way using the LHModalPop component.
 * It automatically appears when an API error occurs and provides options to:
 * - Try again (closes modal)
 * - Contact support (placeholder for future implementation)
 * - Dismiss the error
 * 
 * Features:
 * - Consistent error display across all StandUp components
 * - User-friendly error messages
 * - Error tracking and logging
 * - Responsive design
 */
export const StandUpErrorModal: React.FC = () => {
  const dispatch = useDispatch();
  const { activeError, isErrorModalVisible } = useSelector(
    (state: RootState) => state.standUp.centralizedErrors
  );

  const handleClose = () => {
    if (activeError) {
      // Remove the error from the store and close modal
      dispatch(removeErrorById(activeError.id));
    }
    dispatch(hideErrorModal());
  };

  if (!activeError) {
    return null;
  }

  return (
    <LHModalPop
      visible={isErrorModalVisible}
      onClose={handleClose}
      title={getApiTitle(activeError.apiName)}
      description={activeError.message}
      showFooter={true}
      primaryButtonText="OK"
      secondaryButtonText=""
      onPrimaryPress={handleClose}
      showCloseButton={true}
    />
  );
};

const styles = StyleSheet.create({
  modalMessage: {
    fontSize: 16,
    color: '#3A3A3A',
    lineHeight: 24,
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default StandUpErrorModal;
